<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <slot></slot>

      <div
        v-for="(author, index) in authors.list"
        :key="'position' + index"
        class="mt-5"
      >
        <h3
          v-if="!isEditorialBoard"
          class="my-1 pt-1"
        >
          № {{ index + 1 }}
        </h3>

        <CardRow v-if="isShowIsforeignauthor">
          <v-checkbox
            class="mt-4 rounded-lg mini-text field-edit d-inline-block"
            v-model="authors.list[index].isForeignAuthor"
            label="Иностранный автор"
            hide-details
            dense
            :disabled="isShowMode"
            @change="
              clearName(index)
              updateAuthors()
            "
          />
        </CardRow>

        <CardAutocompleteRow
          v-if="!author.notFoundAuthor && !author.isForeignAuthor"
          v-model="authors.list[index].employee"
          :items="author.employeesList"
          item-value="id"
          item-text="fullName"
          label="ФИО"
          placeholder="Введите не менее 3-х символов"
          return-object
          hide-no-data
          :loading="author.employeesLoading"
          :disabled="isShowMode"
          :required="!isShowMode"
          @input="
            $v.authors.list.$each[index].employee.$touch()
            updateAuthors()
          "
          @searchData="getEmployees($event, index)"
          @change="getEmployeePosition($event, index)"
        />

        <CardFieldRow
          v-if="author.notFoundAuthor && !author.isForeignAuthor"
          v-model="authors.list[index].nameRus"
          label="ФИО"
          placeholder="Добавьте ФИО"
          :disabled="isShowMode"
          :error-messages="errorMessages.nameRus"
          :required="!isShowMode"
          @input="
            $v.authors.list.$each[index].nameRus.$touch()
            updateAuthors()
          "
        />

        <CardRow v-if="!hideNotFoundAuthor">
          <v-checkbox
            class="rounded-lg mini-text field-edit d-inline-block"
            v-model="authors.list[index].notFoundAuthor"
            label="Не является ППС Университета"
            hide-details
            dense
            :disabled="isShowMode"
            @change="
              clearFIOAfterNotFoundAuthor(index)
              updateAuthors()
            "
          />
        </CardRow>

        <CardFieldRow
          v-if="isForeignPublication || author.isForeignAuthor"
          v-model="authors.list[index].lastNameEng"
          label="Фамилия (ENG)"
          placeholder="Добавьте Фамилию (ENG)"
          :required="!isShowMode && (isForeignPublication || authors.list[index].isForeignAuthor)"
          :disabled="isShowMode"
          :error-messages="errorMessages[index].lastNameEng"
          @input="
            $v.authors.list.$each[index].lastNameEng.$touch()
            updateAuthors()
          "
        />

        <CardFieldRow
          v-if="isForeignPublication || author.isForeignAuthor"
          v-model="authors.list[index].firstNameEng"
          label="Имя (ENG)"
          placeholder="Добавьте Имя (ENG)"
          :disabled="isShowMode"
          :required="!isShowMode && (isForeignPublication || authors.list[index].isForeignAuthor)"
          :error-messages="errorMessages[index].firstNameEng"
          @input="
            $v.authors.list.$each[index].firstNameEng.$touch()
            updateAuthors()
          "
        />

        <CardFieldRow
          v-if="isForeignPublication || author.isForeignAuthor"
          v-model="authors.list[index].middleNameEng"
          label="Отчество (ENG)"
          placeholder="Добавьте Отчество (ENG)"
          :disabled="isShowMode"
          :error-messages="errorMessages[index].middleNameEng"
          @input="
            $v.authors.list.$each[index].middleNameEng.$touch()
            updateAuthors()
          "
        />

        <template v-if="!authors.list[index].notFoundAuthor && !author.isForeignAuthor">
          <CardRow
            v-if="author.employeePositionsLoading"
            label="Кафедра"
            required
            :hide-border-bottom="hideShare && index + 1 === authors.list.length"
          >
            <div class="d-flex justify-center align-center">
              <v-progress-circular
                :size="22"
                :width="2"
                color="primary"
                indeterminate
              />
            </div>
          </CardRow>

          <SimpleTextRow
            v-else-if="!author.employeePositionsByEmployee?.length"
            name="Кафедра"
            :required="!isShowMode"
            :value="getEmployeePositionWithDuty(author)"
            :hide-border-bottom="hideShare && index + 1 === authors.list.length"
          />

          <CardAutocompleteRow
            v-else
            v-model="authors.list[index].employeePosition"
            :items="getEmployeePositionWithName(author)"
            item-value="id"
            item-text="name"
            label="Кафедра"
            placeholder="Выберите кафедру"
            :disabled="isShowMode"
            :error-messages="errorMessages[index].employeePosition"
            :required="!isShowMode"
            :hide-border-bottom="hideShare && index + 1 === authors.list.length"
            @input="
              $v.authors.list.$each[index].employeePosition.$touch()
              updateAuthors()
            "
          />
        </template>

        <CardFieldRow
          v-if="!hideShare"
          v-model="authors.list[index].share"
          type="number"
          label="Доля авторского участия в публикации, %"
          placeholder="Число от 0.1 до 100"
          :disabled="isShowMode"
          :hide-border-bottom="index + 1 === authors.list.length"
          :error-messages="errorMessages[index].share"
          :required="!isShowMode"
          @input="
            $v.authors.list.$each[index].share.$touch()
            updateAuthors()
          "
        />

        <v-card-actions
          class="pt-2 pb-4 d-flex justify-end mt-1"
          v-if="authors.list.length > 1 && !isShowMode"
        >
          <v-btn
            class="pa-0 mr-1 d-flex align-center simple-button simple-button__remove"
            color="red"
            plain
            :ripple="false"
            @click="removeAuthor(index)"
          >
            <span class="btn-text">Удалить автора</span>
          </v-btn>
        </v-card-actions>
      </div>
    </v-card-text>

    <v-card-actions
      class="px-6 pt-0 pb-4 d-flex justify-end"
      v-if="authors.list.length < maxAuthors && !isShowMode"
    >
      <v-btn
        v-if="!isEditorialBoard"
        class="pa-0 mr-1 d-flex align-center simple-button"
        color="primary"
        plain
        :ripple="false"
        @click="addAuthor"
      >
        <span class="btn-text">Добавить автора</span>
      </v-btn>
    </v-card-actions>

    <CardRow
      v-if="uploadFiles"
      class="my-0 mx-1 bt"
      label="Cоглашение между соавторами"
    >
      <UIUploadFile
        acceptFileFormats=".pdf, .jpeg, .jpg, .doc, .docx"
        button-text="Добавить соглашение между соавторами"
        :maxFileWeightInMb="5"
        :is-show-mode="isShowMode"
        :multiple="false"
        :maxFiles="1"
        :fileList="agreementFileList"
        @uploadFiles="file => $emit('uploadAgreementFile', file)"
        @removeFile="index => $emit('removeAgreementFile', index)"
      />
    </CardRow>
  </v-card>
</template>

<script>
import CardRow from '@/components/ui/row/CardRow'
import SimpleTextRow from '@/components/ui/SimpleTextRow'
import CardAutocompleteRow from '@/components/ui/row/CardAutocompleteRow'
import CardFieldRow from '@/components/ui/row/CardFieldRow'
import { employeePositionApi } from '@/api'
import { between, maxLength, requiredIf } from 'vuelidate/lib/validators'
import { regexFrom0To100WithDecimal, setValidateMessages } from '@/scripts/validation'
import { AuthorWorkParent } from '@/models'
import _debounce from 'lodash/debounce'
import UIUploadFile from '@/components/ui/UIUploadFile.vue'

export default {
  components: {
    UIUploadFile,
    CardFieldRow,
    CardAutocompleteRow,
    SimpleTextRow,
    CardRow
  },

  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    agreementFileList: Array,
    hideShare: {
      type: Boolean,
      default: false
    },
    maxAuthors: {
      type: Number,
      default: 999
    },
    isEditorialBoard: {
      type: Boolean,
      default: false
    },
    isShowIsforeignauthor: {
      type: Boolean,
      default: false
    },
    isForeignPublication: {
      type: Boolean,
      default: false
    },
    hideNotFoundAuthor: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Авторы на языке оригинала публикации'
    },
    uploadFiles: {
      type: Boolean,
      default: false
    }
  },

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    authors: new AuthorWorkParent()
  }),

  validations() {
    return {
      authors: {
        list: {
          $each: {
            employee: { required: requiredIf(item => !item.notFoundAuthor && !item.isForeignAuthor) },
            employeePosition: { required: requiredIf(item => !item.notFoundAuthor && !item.isForeignAuthor) },
            nameRus: {
              required: requiredIf(item => item.notFoundAuthor && !item.isForeignAuthor),
              maxLength: maxLength(300)
            },
            share: {
              required: requiredIf(() => !this.hideShare),
              regexFrom0To100WithDecimal,
              between: between(0.1, 100)
            },
            middleNameEng: { maxLength: maxLength(30) },
            firstNameEng: {
              required: requiredIf(item => this.isForeignPublication || item.isForeignAuthor),
              maxLength: maxLength(30)
            },
            lastNameEng: {
              required: requiredIf(item => this.isForeignPublication || item.isForeignAuthor),
              maxLength: maxLength(30)
            }
          }
        }
      }
    }
  },

  computed: {
    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.authors.list.$each.$params).forEach(listKey => {
        allErrors[listKey] = {}

        Object.keys(this.$v.authors.list.$each[listKey].$params).forEach(key => {
          allErrors[listKey][key] = setValidateMessages(this.$v.authors.list.$each[listKey][key])
        })
      })

      return allErrors
    }
  },

  methods: {
    getEmployeePosition: _debounce(async function (employee, index) {
      if (employee) {
        try {
          this.authors.list[index].employeePositionsLoading = true
          let employeePositionList = await employeePositionApi.findAllByEmployee(employee.id)
          employeePositionList = employeePositionList?.filter(el => el.isActual)

          if (employeePositionList.length === 0) this.$toast.error('У выбранного автора отсутствует кафедра')
          else if (employeePositionList.length === 1)
            this.authors.list[index].employeePosition = employeePositionList[0]
          else this.authors.list[index].employeePositionsByEmployee = employeePositionList

          await this.updateAuthors()
        } catch (error) {
          console.log(error)
          this.showErrorMessage(error, 'Не удалось получить список подразделений')
        } finally {
          this.authors.list[index].employeePositionsLoading = false
        }
      }
    }, 10),

    getEmployeePositionWithName(author) {
      const employeePositionWithName = structuredClone(author.employeePositionsByEmployee)
      for (const employeePosition of employeePositionWithName)
        employeePosition.name = `${employeePosition.department.name} (${employeePosition.duty.name})`
      return employeePositionWithName
    },

    getEmployeePositionWithDuty(author) {
      return author.employeePosition
        ? `${author.employeePosition.department.name} (${author.employeePosition.duty.name})`
        : null
    },

    removeAuthor(index) {
      this.authors.list.splice(index, 1)
      this.$nextTick(() => {
        this.updateAuthors()
      })
    },

    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    addAuthor() {
      this.authors.add()
      this.$nextTick(() => {
        this.updateAuthors()
      })
    },

    async updateAuthors() {
      try {
        this.$emit('update:selected-item', new AuthorWorkParent(this.authors?.list))
        this.updateInvalid()
      } catch (e) {
        this.showErrorMessage(e, 'Ошибка при обновлении')
        throw e
      }
    },

    clearFIOAfterNotFoundAuthor(index) {
      this.authors.list[index].employeePosition = null
      this.authors.list[index].employee = null
      this.authors.list[index].employeesList = []
      this.authors.list[index].nameRus = null
      this.authors.list[index].employeePositionsByEmployee = []
    },

    clearName(index) {
      this.authors.list[index].employeePosition = null
      this.authors.list[index].employee = null
      this.authors.list[index].nameRus = null
      this.authors.list[index].employeesList = []
      this.authors.list[index].employeePositionsByEmployee = []
      if (!this.authors.list[index].isForeignAuthor && !this.isForeignPublication) {
        this.authors.list[index].firstNameEng = null
        this.authors.list[index].middleNameEng = null
        this.authors.list[index].lastNameEng = null
      }
    },

    getEmployees: _debounce(async function (val, index) {
      await this.authors.list[index]?.findEmployees(val)
    }, 500)
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.authors = new AuthorWorkParent(val?.list)
        this.updateInvalid()
        if (!this.authors.list.length) this.addAuthor()
      }
    },

    maxAuthors(val) {
      if (this.authors.list.length) {
        this.authors = new AuthorWorkParent(this.authors.list.slice(0, val))
        this.$nextTick(() => {
          this.updateAuthors()
        })
      }
    },

    isForeignPublication(val) {
      if (!val) {
        this.authors.list.forEach((el, index) => {
          if (!this.authors.list[index].isForeignAuthor) {
            this.authors.list[index].lastNameEng = null
            this.authors.list[index].firstNameEng = null
            this.authors.list[index].middleNameEng = null
          }
        })
      }
      this.updateAuthors()
    }
  }
}
</script>

<style scoped></style>
